// File: `ArgosFrontend/src/containers/pages/realWorldObjects/Signs/RoarReference.tsx`
import React, { useRef, useEffect, useState } from "react";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import { renderUpdateID } from "../CommonFields";
import { usePostRequest } from "../../../../api/helper";

export interface IProps {
  object: any;
  onDataUpdate: (data: any) => void;
}

const RoarReference = (props: IProps) => {
  // ref for the visible (read-only) ID input
  const roarIdVisibleRef = useRef<HTMLInputElement>(null);
  // ref specifically for the dialog input (must be separate)
  const roarIdDialogRef = useRef<HTMLInputElement>(null);
  const roarUrlRef = useRef<HTMLInputElement>(null);

  const [roarHostname, setRoarHostname] = useState<string>("");
  const [roarId, setRoarId] = useState<string>(props.object?.roarId ?? "");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const fetchRoarUrlHandle = usePostRequest('/HttpTriggerRwo?type=getRoarUrl');
  // Fetch hostname on mount
  useEffect(() => {
    let cancelled = false;
    fetchRoarUrlHandle()
      .then(data => {
        if (!cancelled && data && typeof data.url === "string") {
          setRoarHostname(data.url);
        }
      })
      .catch(() => {
        // silently ignore fetch errors; leave hostname empty
      });
    return () => { cancelled = true; };
  }, []);

  // Sync local roarId when props.object changes
  useEffect(() => {
    const incomingId = props.object?.roarId ?? "";
    setRoarId(incomingId);
    if (roarIdVisibleRef.current) roarIdVisibleRef.current.value = incomingId;
    if (roarUrlRef.current) roarUrlRef.current.value = props.object?.roarUrl ?? "";
  }, [props.object]);

  const inputStyle = {
    width: "100%",
    minWidth: "80px",
    maxWidth: "100%",
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    minHeight: "48px",
  };

  // Build constructed URL from hostname and current ID.
  // If ID is empty, URL must be empty.
  const constructedUrl = (roarHostname && roarId) ? `${roarHostname}?id=${encodeURIComponent(roarId)}` : "";

  /**
   * Handle save from the dialog.
   * - Always update roarId.
   * - If newId is non-empty generate and set the URL.
   * - If newId is empty, clear the URL (set to empty string).
   */
  const handleUpdateID = (newId: string) => {
    const trimmedId = (newId ?? "").toString().trim();
    setRoarId(trimmedId);

    // update visible input immediately
    if (roarIdVisibleRef.current) roarIdVisibleRef.current.value = trimmedId;

    // prepare payload for parent update
    const payload: any = { roarId: trimmedId };

    if (trimmedId) {
      const newUrl = roarHostname ? `${roarHostname}?id=${encodeURIComponent(trimmedId)}` : "";
      payload.roarUrl = newUrl;
      if (roarUrlRef.current) roarUrlRef.current.value = newUrl;
      if (props.object) props.object.roarUrl = newUrl;
    } else {
      // when id is empty, url must be empty
      payload.roarUrl = "";
      if (roarUrlRef.current) roarUrlRef.current.value = "";
      if (props.object) props.object.roarUrl = "";
    }

    // call parent with updated fields
    props.onDataUpdate(payload);
    // update object's roarId
    if (props.object) props.object.roarId = trimmedId;
  };

  return (
    <ArgosFlexbox vertical style={{ padding: 16 }}>
      <div style={{ flex: 1, minWidth: "auto", display: "flex", alignItems: "center" }}>
        <div style={{ flexGrow: 1 }}>
          <label htmlFor="roarId" style={{ display: "block", marginBottom: "5px", color: "#0173CE" }}>
            ID
          </label>
          <input
            id="roarId"
            type="text"
            ref={roarIdVisibleRef}
            value={roarId}
            style={inputStyle}
            disabled={true}
            readOnly
            onChange={() => {}}
          />
        </div>
        {renderUpdateID(roarId, handleUpdateID, dialogOpen, setDialogOpen, roarIdDialogRef, error, setError)}
      </div>

      <div style={{ flex: 1, minWidth: "auto", display: "flex", alignItems: "center" }}>
        <div style={{ flexGrow: 1 }}>
          <label htmlFor="roarUrl" style={{ display: "block", marginBottom: "5px", color: "#0173CE" }}>
            URL
          </label>
          <input
            id="roarUrl"
            type="text"
            ref={roarUrlRef}
            value={constructedUrl}
            style={inputStyle}
            disabled={true}
            readOnly
          />
        </div>
      </div>
    </ArgosFlexbox>
  );
};

export default RoarReference;
